<template>
  <!-- Sign-in, forgot / reset / change password: redesigned shell
       (form column + illustrated band). -->
  <main
    v-if="usesNewShell"
    class="login-shell"
    :class="{
      'login-shell--light': isForgot,
      'login-shell--wide': isPasswordChange
    }"
    dir="rtl"
  >
    <!-- Phones and tablets: the wide band is replaced by a thin animated
         strip (kids play ball until a grown-up turns up) and a wave below. -->
    <div class="login-shell__strip" aria-hidden="true">
      <span
        v-for="bar in stripBars"
        :key="bar.id"
        class="login-shell__bar"
        :style="{ height: bar.height, animationDelay: bar.delay }"
      ></span>

      <!-- Mockup scene, 19.2s loop: the kids pass the ball (ball, kicks and
           jump run on their own fast 1.6-3.2s cycles), a grown-up walks in,
           shouts and sends them off; they come back before it starts over. -->
      <svg class="ls-yard" viewBox="0 0 300 54">
        <g class="ls-yard__kid1">
          <g class="ls-yard__face1">
            <g>
              <path
                d="M62 40 L59 53"
                stroke="#1f3b63"
                stroke-width="3.4"
                stroke-linecap="round"
              />
              <g class="ls-yard__kick-a">
                <path
                  d="M62 40 L64 53"
                  stroke="#1f3b63"
                  stroke-width="3.4"
                  stroke-linecap="round"
                />
              </g>
              <path
                d="M62 31 L56 37.5 M62 31 L68 36"
                stroke="#f2c29b"
                stroke-width="2.6"
                stroke-linecap="round"
              />
              <rect
                x="57"
                y="28"
                width="10"
                height="13.5"
                rx="4"
                fill="#ffd166"
              />
              <circle cx="62" cy="22" r="5.3" fill="#f2c29b" />
              <path d="M56.5 21.6 A5.5 5.5 0 0 1 67.5 21.6 Z" fill="#3b2a20" />
            </g>
          </g>
        </g>

        <g class="ls-yard__kid2">
          <g class="ls-yard__face2">
            <g class="ls-yard__jump">
              <path
                d="M150 40 L147 53"
                stroke="#1f3b63"
                stroke-width="3.4"
                stroke-linecap="round"
              />
              <path
                d="M150 40 L152 53"
                stroke="#1f3b63"
                stroke-width="3.4"
                stroke-linecap="round"
              />
              <path
                d="M150 31 L144 37.5 M150 31 L156 36"
                stroke="#c98b5e"
                stroke-width="2.6"
                stroke-linecap="round"
              />
              <rect
                x="145"
                y="28"
                width="10"
                height="13.5"
                rx="4"
                fill="#6dc86d"
              />
              <circle cx="150" cy="22" r="5.3" fill="#c98b5e" />
              <path
                d="M144.5 21.6 A5.5 5.5 0 0 1 155.5 21.6 Z"
                fill="#3b2a20"
              />
            </g>
          </g>
          <g class="ls-yard__call">
            <rect x="158" y="6" width="30" height="13" rx="6.5" fill="#fff" />
            <text
              x="173"
              y="16"
              text-anchor="middle"
              font-size="10"
              font-weight="800"
              fill="#2f80ed"
            >
              בואו!
            </text>
          </g>
        </g>

        <g class="ls-yard__kid3">
          <g class="ls-yard__face3">
            <g>
              <path
                d="M238 40 L241 53"
                stroke="#1f3b63"
                stroke-width="3.4"
                stroke-linecap="round"
              />
              <g class="ls-yard__kick-b">
                <path
                  d="M238 40 L236 53"
                  stroke="#1f3b63"
                  stroke-width="3.4"
                  stroke-linecap="round"
                />
              </g>
              <path
                d="M238 31 L244 37.5 M238 31 L232 36"
                stroke="#ffdcbc"
                stroke-width="2.6"
                stroke-linecap="round"
              />
              <rect
                x="233"
                y="28"
                width="10"
                height="13.5"
                rx="4"
                fill="#fff"
              />
              <circle cx="238" cy="22" r="5.3" fill="#ffdcbc" />
              <path
                d="M232.5 21.6 A5.5 5.5 0 0 1 243.5 21.6 Z"
                fill="#3b2a20"
              />
            </g>
          </g>
        </g>

        <g class="ls-yard__ball">
          <g class="ls-yard__ball-x">
            <g class="ls-yard__ball-y">
              <g class="ls-yard__ball-spin">
                <circle
                  cx="70"
                  cy="49.5"
                  r="4.4"
                  fill="#fff"
                  stroke="#1f3b63"
                  stroke-width="1"
                />
                <path d="M70 47.2l2 1.5-.8 2.3h-2.4l-.8-2.3z" fill="#1f3b63" />
              </g>
            </g>
          </g>
        </g>

        <g class="ls-yard__adult">
          <g class="ls-yard__adult-face">
            <g>
              <g class="ls-yard__adult-leg-a">
                <path
                  d="M180 36 L180 54"
                  stroke="#1f3b63"
                  stroke-width="4"
                  stroke-linecap="round"
                />
              </g>
              <g class="ls-yard__adult-leg-b">
                <path
                  d="M180 36 L180 54"
                  stroke="#1f3b63"
                  stroke-width="4"
                  stroke-linecap="round"
                />
              </g>
              <path
                d="M180 21 L183 32"
                stroke="#e0a67a"
                stroke-width="3"
                stroke-linecap="round"
              />
              <rect
                x="174"
                y="18"
                width="12"
                height="19.5"
                rx="5"
                fill="#ff8a5b"
              />
              <g class="ls-yard__adult-arm">
                <path
                  d="M180 21 L180 32"
                  stroke="#e0a67a"
                  stroke-width="3"
                  stroke-linecap="round"
                />
              </g>
              <circle cx="180" cy="11" r="5.8" fill="#e0a67a" />
              <path d="M174 10.6 A6 6 0 0 1 186 10.6 Z" fill="#2b2b2b" />
            </g>
          </g>
          <g class="ls-yard__shout">
            <rect x="146" y="0" width="24" height="13" rx="6.5" fill="#fff" />
            <text
              x="158"
              y="10"
              text-anchor="middle"
              font-size="10"
              font-weight="800"
              fill="#d92d20"
            >
              !!!
            </text>
          </g>
        </g>
      </svg>
    </div>

    <section class="login-shell__panel">
      <div class="login-shell__inner">
        <div class="login-shell__logos">
          <img
            v-if="campusLogo"
            class="login-shell__campus-logo"
            :src="campusLogo"
            :alt="campus.name || ''"
            @error="campusLogoFailed = true"
          />
          <router-link
            :to="{ name: 'HomePage' }"
            class="login-shell__brand"
            aria-label="Smart Campus"
          >
            <img src="/images/smart-campus-logo.svg" alt="Smart Campus" />
          </router-link>
        </div>

        <slot />

        <footer class="login-shell__footer">
          <a
            href="https://www.smart-campus.co.il"
            target="_blank"
            rel="noopener"
            class="login-shell__footer-main"
          >
            לא המוסד שלך?
          </a>
          <nav class="login-shell__footer-links">
            <a
              href="https://help.smart-campus.co.il/he/"
              target="_blank"
              rel="noopener"
            >
              עזרה
            </a>
            <a
              href="https://www.smart-campus.co.il/privacy/"
              target="_blank"
              rel="noopener"
            >
              פרטיות
            </a>
          </nav>
        </footer>
      </div>
    </section>

    <aside class="login-shell__visual" aria-hidden="true">
      <span class="login-shell__ring login-shell__ring--top"></span>
      <span class="login-shell__ring login-shell__ring--bottom"></span>
      <template v-if="isSignIn">
        <span class="login-shell__dot login-shell__dot--a"></span>
        <span class="login-shell__dot login-shell__dot--b"></span>
      </template>

      <div v-if="isSignIn" class="login-shell__stage">
        <!-- bar chart card -->
        <div class="ls-card ls-card--bars">
          <div class="ls-card__head">
            <span class="ls-card__avatar"></span>
            <span class="ls-card__line"></span>
          </div>
          <div class="ls-card__bars">
            <span style="height: 46%; background: #8ec3ff"></span>
            <span style="height: 74%; background: #3d93ff"></span>
            <span style="height: 58%; background: #6dc86d"></span>
            <span style="height: 88%; background: #ffd166"></span>
            <span style="height: 36%; background: #cfe2fa"></span>
          </div>
        </div>

        <!-- line chart card -->
        <div class="ls-card ls-card--lines">
          <div class="ls-card__head">
            <span class="ls-card__line ls-card__line--short"></span>
            <span class="ls-card__line ls-card__line--tiny"></span>
          </div>
          <svg
            class="ls-card__chart"
            viewBox="0 0 120 48"
            preserveAspectRatio="none"
          >
            <path
              class="ls-card__trend"
              d="M2 40C18 40 24 14 40 16s20 20 34 12 26-30 42-32"
            />
            <path
              class="ls-card__trend-bg"
              d="M2 44C20 44 30 30 46 30s22 8 36 2 24-16 34-18"
            />
          </svg>
        </div>

        <!-- status pills -->
        <div class="ls-pill ls-pill--status">
          <span class="ls-pill__dot"></span>
          <span class="ls-pill__line"></span>
        </div>
        <div class="ls-pill ls-pill--small"></div>
      </div>

      <!-- forgot password: a sheet leaving an envelope, a key swinging above -->
      <div v-else-if="isForgot" class="login-shell__stage">
        <svg viewBox="0 0 200 150" class="ls-scene">
          <g class="ls-forgot__sheet">
            <rect
              x="72"
              y="26"
              width="56"
              height="52"
              rx="5"
              fill="#fff"
              stroke="#bcd6f2"
              stroke-width="2"
            />
            <path
              d="M82 40h36M82 48h36M82 56h22"
              stroke="#d8e6f7"
              stroke-width="3.4"
              stroke-linecap="round"
            />
          </g>
          <g>
            <rect
              x="52"
              y="62"
              width="96"
              height="58"
              rx="8"
              fill="#fff"
              stroke="#bcd6f2"
              stroke-width="2"
            />
            <path
              d="M53 68l47 32 47-32"
              fill="none"
              stroke="#bcd6f2"
              stroke-width="2"
              stroke-linejoin="round"
            />
            <path
              d="M53 114l34-26M147 114l-34-26"
              fill="none"
              stroke="#e3edf9"
              stroke-width="2"
            />
          </g>
          <g class="ls-forgot__key">
            <circle
              cx="160"
              cy="34"
              r="11"
              fill="none"
              stroke="#f2b033"
              stroke-width="4.2"
            />
            <circle cx="160" cy="34" r="3.2" fill="#f2b033" />
            <path
              d="M152 42l-17 17M141 53l5 5M136 58l4 4"
              fill="none"
              stroke="#f2b033"
              stroke-width="4.2"
              stroke-linecap="round"
            />
          </g>
          <g class="ls-forgot__stars" fill="#2f80ed">
            <circle cx="34" cy="40" r="4" />
            <circle cx="44" cy="96" r="2.6" opacity=".6" />
            <circle cx="172" cy="96" r="3.2" opacity=".5" />
          </g>
        </svg>
      </div>
      <!-- password change: an intruder with a key bounces off the shield -->
      <div v-else class="login-shell__stage">
        <svg viewBox="0 0 200 150" class="ls-scene">
          <ellipse cx="100" cy="132" rx="72" ry="6" fill="rgba(11,45,92,.18)" />

          <g class="ls-guard__intruder">
            <g class="ls-guard__bob">
              <path d="M34 128v-24a14 14 0 0128 0v24z" fill="#15427c" />
              <path d="M36 98a12 12 0 0124 0z" fill="#0e3364" />
              <circle cx="48" cy="88" r="10.5" fill="#15427c" />
              <path d="M37.5 88a10.5 10.5 0 0121 0z" fill="#0e3364" />
              <path
                d="M60 104l14-6"
                stroke="#15427c"
                stroke-width="5"
                stroke-linecap="round"
              />
              <circle
                cx="79"
                cy="95"
                r="4.6"
                fill="none"
                stroke="#f2b033"
                stroke-width="3"
              />
              <path
                d="M83 98l7 7M87 102l-3 3"
                stroke="#f2b033"
                stroke-width="3"
                stroke-linecap="round"
              />
            </g>
          </g>

          <g>
            <path d="M150 128v-28a15 15 0 0130 0v28z" fill="#fff" />
            <circle cx="165" cy="80" r="11" fill="#fff" />
            <path d="M153 74a12 12 0 0124 0z" fill="#dce9fa" />
            <path
              d="M150 104l-14-6"
              stroke="#fff"
              stroke-width="5.5"
              stroke-linecap="round"
            />
          </g>

          <g class="ls-guard__shield">
            <path
              d="M124 52l26 9v22c0 17-11 30-26 36-15-6-26-19-26-36V61z"
              fill="rgba(255,255,255,.22)"
              stroke="#fff"
              stroke-width="2.6"
              stroke-linejoin="round"
            />
            <path
              class="ls-guard__shackle"
              d="M117 86v-6a7 7 0 0114 0v6"
              fill="none"
              stroke="#fff"
              stroke-width="3"
              stroke-linecap="round"
            />
            <rect x="112" y="86" width="24" height="18" rx="4" fill="#fff" />
            <path
              d="M124 92v6"
              stroke="#2f80ed"
              stroke-width="2.8"
              stroke-linecap="round"
            />
          </g>

          <g class="ls-guard__stars" fill="#fff">
            <circle cx="30" cy="40" r="3.6" />
            <circle cx="172" cy="46" r="3" />
            <circle cx="96" cy="28" r="2.4" />
          </g>
          <g class="ls-guard__ripple">
            <circle
              cx="112"
              cy="88"
              r="16"
              fill="none"
              stroke="#fff"
              stroke-width="2.6"
            />
          </g>
          <g class="ls-guard__block">
            <circle cx="104" cy="88" r="12.5" fill="#f04438" />
            <path
              d="M99.5 83.5l9 9M108.5 83.5l-9 9"
              stroke="#fff"
              stroke-width="3.2"
              stroke-linecap="round"
            />
          </g>
        </svg>
      </div>
    </aside>
    <div class="login-shell__wave" aria-hidden="true">
      <svg viewBox="0 0 1280 28" preserveAspectRatio="none">
        <path
          d="M0 14 C20 4 20 4 40 14 S60 24 80 14 C100 4 100 4 120 14 S140 24 160 14 C180 4 180 4 200 14 S220 24 240 14 C260 4 260 4 280 14 S300 24 320 14 C340 4 340 4 360 14 S380 24 400 14 C420 4 420 4 440 14 S460 24 480 14 C500 4 500 4 520 14 S540 24 560 14 C580 4 580 4 600 14 S620 24 640 14 C660 4 660 4 680 14 S700 24 720 14 C740 4 740 4 760 14 S780 24 800 14 C820 4 820 4 840 14 S860 24 880 14 C900 4 900 4 920 14 S940 24 960 14 C980 4 980 4 1000 14 S1020 24 1040 14 C1060 4 1060 4 1080 14 S1100 24 1120 14 C1140 4 1140 4 1160 14 S1180 24 1200 14 C1220 4 1220 4 1240 14 S1260 24 1280 14"
          fill="none"
          stroke="#9cc6f7"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    </div>
  </main>

  <!-- Other auth pages keep the previous shell. -->
  <main v-else>
    <div class="entry-block">
      <div class="entry-block__right">
        <div class="entry-block__header">
          <router-link :to="{ name: 'HomePage' }" class="entry-block__logo">
            <!-- <img src="/images/smart-campus-logo.svg" alt=""> -->
            <img src="/images/smart-campus-logo.svg" alt="" />
          </router-link>
        </div>

        <div class="campus-info" v-if="campus">
          <img
            class="campus-info__logo"
            :src="campus.logo"
            :alt="campus.name || 'Campus'"
          />
          <!-- <div class="campus-info__name">{{ campus.name }}</div> -->
        </div>
        <slot />
        <div class="link-campus-info">
          <a href="https://www.smart-campus.co.il" class="link-text">
            לא המוסד שלך?
          </a>
        </div>
        <ul class="langs">
          <!-- <li class="langs__item">
                        <a href="" class="langs__item-link">
                            <img src="/images/russialanguage.svg" alt="">
                        </a>
                    </li> -->
          <li class="langs__item active">
            <a href="" class="langs__item-link">
              <img src="/images/hebrewlanguage.svg" alt="" />
            </a>
          </li>
        </ul>
      </div>
      <div class="entry-block__left">
        <template v-if="$route.name === 'Confirm'">
          <div class="sky"></div>
          <img class="bushes-1" src="/images/Bushes.svg" alt="" />
          <div id="confirmAnimation">
            <SingInAnimation :animationOptions="ConfirmAnimation" />
          </div>
          <img class="bushes-2" src="/images/Bushes.svg" alt="" />
          <div class="ground"></div>
        </template>
        <template v-else>
          <div class="entry-block__left-bg">
            <img src="/images/entry-block-bg.svg" alt="" />
          </div>
          <div class="entry-block__content">
            <h1 class="entry-block__title">
              <span>להרגיש</span>
              <span>את העוצמה</span>
              <span>של הקדמה</span>
            </h1>
            <div class="entry-block__text">
              <p>מערכת פשוטה המאגדת את כל ניהול המוסד</p>
              <p>במקום אחד</p>
            </div>
          </div>
        </template>
      </div>
    </div>
  </main>
</template>
<script>
import ConfirmAnimation from '@/Components/Layout/jsons/ConfirmAnimation.json';
import SingInAnimation from '@/Components/Layout/Animations/SingInAnimation';

export default {
  name: 'AuthLayout',
  components: {
    SingInAnimation
  },
  data() {
    return {
      campus: null,
      campusLogoFailed: false,
      ConfirmAnimation: { animationData: ConfirmAnimation },
      animationSpeed: 1
    };
  },
  computed: {
    isSignIn() {
      return this.$route.name === 'SignIn';
    },
    // Bars of the mobile strip: fixed pseudo-random heights so the skyline
    // looks uneven, with the wave of the animation running across them.
    stripBars() {
      const count = 70;
      const bars = [];
      for (let i = 0; i < count; i++) {
        const noise = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1;
        bars.push({
          id: i,
          height: `${Math.round(30 + noise * 55)}%`,
          // spread over ~1.6s, as in the mockup: a fine ripple across the
          // skyline rather than a wave marching bar by bar
          delay: `${((i / count) * 1.6).toFixed(2)}s`
        });
      }

      return bars;
    },
    isForgot() {
      return this.$route.name === 'ForgotPassword';
    },
    isPasswordChange() {
      return (
        this.$route.name === 'ResetPassword' ||
        this.$route.name === 'FirstChangePassword'
      );
    },
    usesNewShell() {
      return this.isSignIn || this.isForgot || this.isPasswordChange;
    },
    campusLogo() {
      if (!this.campus || !this.campus.logo || this.campusLogoFailed) {
        return null;
      }
      return this.campus.logo;
    }
  },
  mounted() {
    fetch('/api/guest/get-campus-info', {
      headers: { Accept: 'application/json' }
    })
      .then(r => r.json())
      .then(j => {
        this.campus = j && j.data ? j.data : null;
      })
      .catch(() => {
        this.campus = null;
      });
  }
};
</script>

<style>
/* Self-hosted Assistant (SIL OFL), variable 200–800, used by the sign-in page. */
@font-face {
  font-family: 'Assistant';
  font-style: normal;
  font-weight: 200 800;
  font-display: swap;
  src: url('../../../css/fonts/assistant/assistant-hebrew.woff2')
    format('woff2');
  unicode-range: U+0307-0308, U+0590-05FF, U+200C-2010, U+20AA, U+25CC,
    U+FB1D-FB4F;
}
@font-face {
  font-family: 'Assistant';
  font-style: normal;
  font-weight: 200 800;
  font-display: swap;
  src: url('../../../css/fonts/assistant/assistant-latin.woff2') format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA,
    U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193,
    U+2212, U+2215, U+FEFF, U+FFFD;
}

/* ================= auth form (sign-in, forgot password) ================= */
.sc-login.entry-block__form {
  max-width: none;
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-family: 'Assistant', 'Rubik', system-ui, sans-serif;
  text-align: right;
}

.sc-login__head {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.sc-login__title {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
  color: #1d2939;
  text-align: right;
}
.sc-login__subtitle {
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: #667085;
  text-align: right;
  text-wrap: pretty;
}

.sc-login__notices {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sc-login .error-notice {
  border-radius: 10px;
  padding: 9px 14px;
  line-height: 1.45;
}
.sc-login .error-notice svg {
  min-width: 24px;
}

.sc-login__fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.sc-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sc-field__label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.sc-field__label {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  text-align: right;
}
.sc-login__link {
  font-size: 13.5px;
  font-weight: 600;
  color: #2f80ed;
  text-decoration: none;
}
.sc-login__link:hover {
  color: #1b64c9;
  text-decoration: underline;
}
.sc-field__control {
  position: relative;
  display: flex;
  align-items: center;
}
.sc-field__icon {
  position: absolute;
  right: 14px;
  width: 18px;
  height: 18px;
  color: #98a2b3;
  pointer-events: none;
}
.sc-field__input {
  width: 100%;
  height: 48px;
  box-sizing: border-box;
  padding: 0 44px 0 14px;
  border: 1px solid #d5dce6;
  border-radius: 11px;
  background: #fff;
  font-family: inherit;
  font-size: 16px;
  color: #1d2939;
  text-align: right;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.sc-field__input::placeholder {
  color: #9aa4b2;
}
.sc-field__input:focus {
  border-color: #2f80ed;
  box-shadow: 0 0 0 4px rgba(47, 128, 237, 0.14);
}
.sc-field__input--error {
  border-color: #f04438;
}
.sc-field__input--error:focus {
  box-shadow: 0 0 0 4px rgba(240, 68, 56, 0.12);
}
.sc-login__actions {
  display: flex;
  align-items: stretch;
  gap: 10px;
}
.sc-login__submit {
  flex: 1;
  min-width: 0;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 0;
  border-radius: 12px;
  background: #2f80ed;
  color: #fff;
  font-family: inherit;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px -8px rgba(47, 128, 237, 0.7);
  transition: background 0.15s, box-shadow 0.15s;
}
.sc-login__submit:hover:not(:disabled) {
  background: #1b6fd8;
}
.sc-login__submit:disabled {
  background: #b9cdea;
  box-shadow: none;
  cursor: default;
}
.sc-login__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: sc-login-spin 0.8s linear infinite;
}
@keyframes sc-login-spin {
  to {
    transform: rotate(360deg);
  }
}

/* muted helper line under a field (icon + text) */
.sc-login__hint {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: #667085;
  text-align: right;
  text-wrap: pretty;
}
.sc-login__hint svg {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  margin-top: 3px;
  color: #98a2b3;
}
/* outlined neutral button next to the primary one */
.sc-login__secondary {
  flex: 0 0 auto;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 22px;
  border: 1px solid #d5dce6;
  border-radius: 12px;
  background: #fff;
  color: #475467;
  font-family: inherit;
  font-size: 15.5px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
}
.sc-login__secondary:hover {
  background: #f7f9fc;
  color: #475467;
  text-decoration: none;
}
.sc-login__back {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  font-weight: 600;
  color: #2f80ed;
  text-decoration: none;
}
.sc-login__back:hover {
  color: #1b64c9;
  text-decoration: underline;
}
.sc-login__back svg {
  width: 17px;
  height: 17px;
}

/* passwords are typed LTR but stay aligned with the rest of the form */
.sc-field__input--password {
  direction: ltr;
  text-align: right;
  padding-left: 46px;
}
.sc-field__eye {
  position: absolute;
  left: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}
.sc-field__eye:hover {
  background: #f2f4f7;
  color: #667085;
}
.sc-field__eye svg {
  width: 18px;
  height: 18px;
}

/* ---- password change forms ---- */
.sc-login--password.entry-block__form {
  gap: 14px;
}
.sc-pw-strength {
  font-size: 12.5px;
  font-weight: 700;
}
.sc-pw-strength--weak {
  color: #b42318;
}
.sc-pw-strength--fair {
  color: #b54708;
}
.sc-pw-strength--strong {
  color: #1d7a48;
}
.sc-field__input--ok {
  border-color: #7cc79b;
}
.sc-field__input--mismatch {
  border-color: #f5a3a3;
}
.sc-field__msg {
  margin: 0;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.4;
  text-align: right;
}
.sc-field__msg--ok {
  color: #1d7a48;
}
.sc-field__msg--error {
  color: #b42318;
}
/* a message under the strength bar (slot of AuthPasswordRules) hugs the bar */
.sc-pw__bar + .sc-field__msg {
  margin-top: -8px;
}
</style>

<style scoped>
/* ================= new sign-in shell ================= */
.login-shell {
  /* grow with the content (a global "main { height: 100% }" would pin it to
     the viewport and let the band spill out); at least one screen tall */
  height: auto;
  min-height: 100vh;
  /* mobile browsers: the height left after their toolbars */
  min-height: 100dvh;
  display: flex;
  align-items: stretch;
  background: #fff;
  font-family: 'Assistant', 'Rubik', system-ui, sans-serif;
}
.login-shell__panel {
  flex: 1 1 460px;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px max(22px, 6%);
}
.login-shell__inner {
  width: 100%;
  max-width: 390px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.login-shell__logos {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.login-shell__brand {
  display: block;
}
.login-shell__brand img,
.login-shell__campus-logo {
  display: block;
  height: 38px;
  width: auto;
  max-width: 170px;
  object-fit: contain;
}
.login-shell__footer {
  margin-top: 4px;
  padding-top: 14px;
  border-top: 1px solid #eef1f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  font-size: 13.5px;
}
.login-shell__footer a {
  color: #5b6472;
  text-decoration: none;
}
.login-shell__footer a:hover {
  color: #1b64c9;
  text-decoration: underline;
}
.login-shell__footer .login-shell__footer-main {
  color: #2f80ed;
  font-weight: 600;
}
.login-shell__footer-links {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* ================= illustrated band ================= */
.login-shell__visual {
  flex: 1 1 420px;
  min-width: 0;
  position: relative;
  overflow: hidden;
  background: linear-gradient(160deg, #3d93ff 0%, #2f80ed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
}
.login-shell__ring {
  position: absolute;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
}
.login-shell__ring--top {
  width: 340px;
  height: 340px;
  top: -120px;
  right: -90px;
}
.login-shell__ring--bottom {
  width: 520px;
  height: 520px;
  bottom: -260px;
  left: -140px;
  border-color: rgba(255, 255, 255, 0.16);
}
.login-shell__dot {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.33);
}
.login-shell__dot--a {
  width: 14px;
  height: 14px;
  top: 22%;
  left: 18%;
}
.login-shell__dot--b {
  width: 8px;
  height: 8px;
  bottom: 26%;
  right: 22%;
}
.login-shell__stage {
  position: relative;
  width: min(80%, 420px);
  aspect-ratio: 4 / 3;
}
.ls-card {
  position: absolute;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.ls-card--bars {
  right: 2%;
  top: 6%;
  width: 60%;
  height: 48%;
  border-radius: 7% / 9%;
  box-shadow: 0 14px 30px -14px rgba(11, 45, 92, 0.55);
  padding: 7% 7% 8%;
  gap: 8%;
  animation: ls-drift-a 7s ease-in-out infinite alternate;
}
.ls-card--lines {
  left: 2%;
  bottom: 8%;
  width: 66%;
  height: 42%;
  border-radius: 6% / 9%;
  box-shadow: 0 18px 34px -16px rgba(11, 45, 92, 0.6);
  padding: 6%;
  gap: 6%;
  animation: ls-drift-b 8s ease-in-out infinite alternate;
}
.ls-card__head {
  display: flex;
  align-items: center;
  gap: 5%;
}
.ls-card__avatar {
  width: 9%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #dbe7f6;
  flex: 0 0 auto;
}
.ls-card__line {
  flex: 1;
  height: 7px;
  border-radius: 4px;
  background: #e8eef7;
}
.ls-card__line--short {
  flex: 0 0 22%;
  height: 6px;
}
.ls-card__line--tiny {
  flex: 0 0 12%;
  height: 6px;
}
.ls-card__bars {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 6%;
}
.ls-card__bars span {
  flex: 1;
  border-radius: 4px 4px 2px 2px;
  transform-origin: bottom;
  animation: ls-bar 3.4s ease-in-out infinite alternate;
}
.ls-card__bars span:nth-child(2) {
  animation-delay: 0.25s;
}
.ls-card__bars span:nth-child(3) {
  animation-delay: 0.5s;
}
.ls-card__bars span:nth-child(4) {
  animation-delay: 0.75s;
}
.ls-card__bars span:nth-child(5) {
  animation-delay: 1s;
}
.ls-card__chart {
  flex: 1;
  width: 100%;
  overflow: visible;
}
.ls-card__trend {
  fill: none;
  stroke: #6dc86d;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 190;
  animation: ls-draw 5s ease-in-out infinite;
}
.ls-card__trend-bg {
  fill: none;
  stroke: #bcd9f8;
  stroke-width: 2.5;
  stroke-linecap: round;
}
.ls-pill {
  position: absolute;
  border-radius: 99px;
  box-shadow: 0 10px 22px -12px rgba(11, 45, 92, 0.5);
}
.ls-pill--status {
  left: 6%;
  top: 4%;
  width: 30%;
  height: 14%;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  gap: 8%;
  padding: 0 8%;
  box-sizing: border-box;
  animation: ls-drift-c 6s ease-in-out infinite alternate;
}
.ls-pill__dot {
  width: 16%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #6dc86d;
  flex: 0 0 auto;
}
.ls-pill__line {
  flex: 1;
  height: 5px;
  border-radius: 4px;
  background: #e2eaf5;
}
.ls-pill--small {
  right: 10%;
  bottom: 2%;
  width: 20%;
  height: 11%;
  background: rgba(255, 255, 255, 0.85);
  animation: ls-drift-c 7.5s 0.8s ease-in-out infinite alternate;
}

@keyframes ls-drift-a {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-8px);
  }
}
@keyframes ls-drift-b {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(7px);
  }
}
@keyframes ls-drift-c {
  from {
    transform: translate(0, 0);
  }
  to {
    transform: translate(-4px, -6px);
  }
}
@keyframes ls-bar {
  from {
    transform: scaleY(0.55);
  }
  to {
    transform: scaleY(1);
  }
}
@keyframes ls-draw {
  0% {
    stroke-dashoffset: 190;
  }
  55%,
  100% {
    stroke-dashoffset: 0;
  }
}

/* forgot password: light band with the envelope + key illustration */
.login-shell--light .login-shell__visual {
  background: linear-gradient(160deg, #eaf3ff 0%, #cfe2fa 100%);
}
.login-shell--light .login-shell__ring--top {
  border-color: rgba(47, 128, 237, 0.16);
}
.login-shell--light .login-shell__ring--bottom {
  border-color: rgba(47, 128, 237, 0.12);
}
.ls-scene {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}
.ls-forgot__sheet,
.ls-forgot__key {
  transform-box: fill-box;
  transform-origin: center;
}
.ls-forgot__sheet {
  animation: ls-sheet 3.6s ease-in-out infinite alternate;
}
.ls-forgot__key {
  animation: ls-key 4.4s ease-in-out infinite alternate;
}
.ls-forgot__stars {
  animation: ls-twinkle 2.8s ease-in-out infinite alternate;
}
@keyframes ls-sheet {
  from {
    transform: translateY(6px);
  }
  to {
    transform: translateY(-7px);
  }
}
@keyframes ls-key {
  from {
    transform: rotate(-9deg);
  }
  to {
    transform: rotate(9deg);
  }
}
@keyframes ls-twinkle {
  from {
    opacity: 0.35;
  }
  to {
    opacity: 1;
  }
}

/* password change: wider column for the checklist, guard scene */
.login-shell--wide .login-shell__inner {
  max-width: 430px;
  gap: 18px;
}
.ls-guard__intruder,
.ls-guard__bob,
.ls-guard__shield,
.ls-guard__shackle,
.ls-guard__ripple,
.ls-guard__block {
  transform-box: fill-box;
  transform-origin: center;
}
.ls-guard__bob,
.ls-guard__shackle {
  transform-origin: center bottom;
}
.ls-guard__intruder {
  animation: ls-intruder 6.4s cubic-bezier(0.4, 0, 0.35, 1) infinite;
}
.ls-guard__bob {
  animation: ls-bob 0.9s ease-in-out infinite alternate;
}
.ls-guard__shield {
  animation: ls-shield 6s ease-in-out infinite;
}
.ls-guard__shackle {
  animation: ls-shackle 2.6s ease-in-out infinite alternate;
}
.ls-guard__stars {
  animation: ls-twinkle 3.2s ease-in-out infinite alternate;
}
.ls-guard__ripple {
  animation: ls-ripple 6.4s ease-out infinite;
}
.ls-guard__block {
  animation: ls-block 6.4s ease-in-out infinite;
}
@keyframes ls-intruder {
  0% {
    transform: translateX(-84px) rotate(0deg);
    opacity: 0;
  }
  8% {
    opacity: 1;
  }
  36% {
    transform: translateX(0) rotate(0deg);
  }
  41% {
    transform: translateX(4px) rotate(-4deg);
  }
  47% {
    transform: translateX(-14px) rotate(-16deg);
  }
  60% {
    transform: translateX(-46px) rotate(-10deg);
    opacity: 1;
  }
  76%,
  100% {
    transform: translateX(-84px) rotate(0deg);
    opacity: 0;
  }
}
@keyframes ls-bob {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-2.5px);
  }
}
@keyframes ls-shield {
  0%,
  35% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.09);
  }
  50% {
    transform: scale(0.99);
  }
  60%,
  100% {
    transform: scale(1);
  }
}
@keyframes ls-shackle {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-4px);
  }
}
@keyframes ls-ripple {
  0%,
  36% {
    opacity: 0;
    transform: scale(0.35);
  }
  40% {
    opacity: 0.95;
    transform: scale(0.6);
  }
  56% {
    opacity: 0;
    transform: scale(1.6);
  }
  100% {
    opacity: 0;
    transform: scale(0.35);
  }
}
@keyframes ls-block {
  0%,
  35% {
    opacity: 0;
    transform: scale(0.5);
  }
  39% {
    opacity: 1;
    transform: scale(1.18);
  }
  44%,
  60% {
    opacity: 1;
    transform: scale(1);
  }
  68% {
    opacity: 0;
    transform: scale(0.95);
  }
  100% {
    opacity: 0;
    transform: scale(0.5);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ls-card,
  .ls-pill,
  .ls-card__bars span,
  .ls-card__trend,
  .ls-forgot__sheet,
  .ls-forgot__key,
  .ls-forgot__stars,
  .ls-guard__intruder,
  .ls-guard__bob,
  .ls-guard__shield,
  .ls-guard__shackle,
  .ls-guard__stars,
  .ls-guard__ripple,
  .ls-guard__block,
  .login-shell__bar,
  .login-shell__wave svg,
  .ls-yard__ball,
  .ls-yard__ball-x,
  .ls-yard__ball-y,
  .ls-yard__ball-spin,
  .ls-yard__kick-a,
  .ls-yard__kick-b,
  .ls-yard__jump,
  .ls-yard__adult-leg-a,
  .ls-yard__adult-leg-b,
  .ls-yard__adult-arm,
  .ls-yard__adult,
  .ls-yard__adult-face,
  .ls-yard__shout,
  .ls-yard__call,
  .ls-yard__kid1,
  .ls-yard__kid2,
  .ls-yard__kid3,
  .ls-yard__face1,
  .ls-yard__face2,
  .ls-yard__face3 {
    animation: none;
  }
}

/* ---------- phones and tablets ----------
   The wide illustrated band needs room the small screens do not have, so
   below 900px it gives way to a thin animated strip above the form and a
   wave below it. */
.login-shell__strip,
.login-shell__wave {
  display: none;
}

@media (max-width: 900px) {
  .login-shell {
    flex-direction: column;
  }
  .login-shell__panel {
    flex: 1 1 auto;
    padding: 22px max(18px, 5%);
  }
  .login-shell__visual {
    display: none;
  }

  .login-shell__strip {
    position: relative;
    flex: 0 0 74px;
    height: 74px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 6px;
    padding: 0 12px;
    overflow: hidden;
    background: linear-gradient(160deg, #3d93ff, #2f80ed);
  }
  .login-shell__bar {
    flex: 0 0 8px;
    border-radius: 3px 3px 0 0;
    background: rgba(255, 255, 255, 0.18);
    transform-origin: bottom;
    animation: ls-mb-bar 2.8s ease-in-out infinite alternate;
  }
  .ls-yard {
    /* overflow is visible on purpose: the kids run far past the viewBox and
       are clipped by the strip itself, not cut off at the edge of the svg */
    position: absolute;
    left: 50%;
    bottom: 0;
    height: 88%;
    width: auto;
    transform: translateX(-50%);
    overflow: visible;
  }

  .login-shell__wave {
    flex: 0 0 28px;
    height: 28px;
    display: block;
    overflow: hidden;
    background: #eaf3ff;
  }
  .login-shell__wave svg {
    display: block;
    width: calc(100% + 80px);
    height: 100%;
    animation: ls-mb-wave 7s linear infinite;
  }
}

@keyframes ls-mb-bar {
  0% {
    transform: scaleY(0.45);
  }
  100% {
    transform: scaleY(1);
  }
}
@keyframes ls-mb-wave {
  100% {
    transform: translateX(80px);
  }
}

/* The scene, straight from the mockup. The 19.2s loop only carries the
   story (who walks in, who runs off); the ball, the kicks and the jump keep
   their own fast 1.6-3.2s cycles, or the whole thing crawls. */
.ls-yard__ball {
  animation: ls-yard-show 19.2s linear infinite;
}
.ls-yard__ball-x {
  animation: ls-yard-ball-x 3.2s ease-in-out infinite;
}
.ls-yard__ball-y {
  animation: ls-yard-ball-y 1.6s ease-in-out infinite;
}
.ls-yard__ball-spin {
  transform-box: fill-box;
  transform-origin: center;
  animation: ls-yard-spin 1.6s linear infinite;
}
.ls-yard__kick-a {
  transform-box: fill-box;
  transform-origin: 50% 0%;
  animation: ls-yard-kick-a 3.2s ease-in-out infinite;
}
.ls-yard__kick-b {
  transform-box: fill-box;
  transform-origin: 50% 0%;
  animation: ls-yard-kick-b 3.2s ease-in-out infinite;
}
.ls-yard__jump {
  animation: ls-yard-jump 3.2s ease-in-out infinite;
}
.ls-yard__kid1 {
  animation: ls-yard-move-1 19.2s ease-in-out infinite;
}
.ls-yard__kid2 {
  animation: ls-yard-move-2 19.2s ease-in-out infinite;
}
.ls-yard__kid3 {
  animation: ls-yard-move-3 19.2s ease-in-out infinite;
}
.ls-yard__face1,
.ls-yard__face2,
.ls-yard__face3,
.ls-yard__adult-face {
  transform-box: fill-box;
  transform-origin: center;
}
.ls-yard__face1 {
  animation: ls-yard-face-1 19.2s step-end infinite;
}
.ls-yard__face2 {
  animation: ls-yard-face-2 19.2s step-end infinite;
}
.ls-yard__face3 {
  animation: ls-yard-face-3 19.2s step-end infinite;
}
.ls-yard__call {
  animation: ls-yard-call 19.2s linear infinite;
}
.ls-yard__adult {
  animation: ls-yard-ad-move 19.2s ease-in-out infinite;
}
.ls-yard__adult-face {
  animation: ls-yard-ad-face 19.2s step-end infinite;
}
.ls-yard__adult-leg-a,
.ls-yard__adult-leg-b,
.ls-yard__adult-arm {
  transform-box: fill-box;
  transform-origin: 50% 0%;
}
.ls-yard__adult-leg-a {
  animation: ls-yard-ad-leg-a 19.2s linear infinite;
}
.ls-yard__adult-leg-b {
  animation: ls-yard-ad-leg-b 19.2s linear infinite;
}
.ls-yard__adult-arm {
  animation: ls-yard-ad-arm 19.2s linear infinite;
}
.ls-yard__shout {
  animation: ls-yard-shout 19.2s linear infinite;
}

/* ball: passed back and forth, and taken off the field while they are away */
@keyframes ls-yard-ball-x {
  0%,
  100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(160px);
  }
}
@keyframes ls-yard-ball-y {
  0%,
  100% {
    transform: translateY(0);
    animation-timing-function: ease-out;
  }
  50% {
    transform: translateY(-26px);
    animation-timing-function: ease-in;
  }
}
@keyframes ls-yard-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes ls-yard-show {
  0%,
  27% {
    opacity: 1;
  }
  29%,
  93% {
    opacity: 0;
  }
  96%,
  100% {
    opacity: 1;
  }
}

/* kids: kick and jump on the spot */
@keyframes ls-yard-kick-a {
  0% {
    transform: rotate(-38deg);
  }
  10%,
  86% {
    transform: rotate(0);
  }
  93% {
    transform: rotate(16deg);
  }
  100% {
    transform: rotate(-38deg);
  }
}
@keyframes ls-yard-kick-b {
  0%,
  36% {
    transform: rotate(0);
  }
  43% {
    transform: rotate(-16deg);
  }
  50% {
    transform: rotate(38deg);
  }
  60%,
  100% {
    transform: rotate(0);
  }
}
@keyframes ls-yard-jump {
  0%,
  16% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(-9px);
  }
  34%,
  66% {
    transform: translateY(0);
  }
  75% {
    transform: translateY(-9px);
  }
  84%,
  100% {
    transform: translateY(0);
  }
}

/* the grown-up walks in at 24-36%, shouts, and leaves at 52-64% */
@keyframes ls-yard-ad-move {
  0%,
  24% {
    transform: translateX(440px);
  }
  36%,
  52% {
    transform: translateX(0);
  }
  64%,
  100% {
    transform: translateX(440px);
  }
}
@keyframes ls-yard-ad-face {
  0% {
    transform: scaleX(1);
  }
  52% {
    transform: scaleX(-1);
  }
  100% {
    transform: scaleX(-1);
  }
}
@keyframes ls-yard-ad-leg-a {
  0%,
  23.99% {
    transform: rotate(0deg);
  }
  24%,
  26%,
  28%,
  30%,
  32%,
  34%,
  36% {
    transform: rotate(22deg);
  }
  25%,
  27%,
  29%,
  31%,
  33%,
  35% {
    transform: rotate(-22deg);
  }
  36.01%,
  51.99% {
    transform: rotate(0deg);
  }
  52%,
  54%,
  56%,
  58%,
  60%,
  62%,
  64% {
    transform: rotate(22deg);
  }
  53%,
  55%,
  57%,
  59%,
  61%,
  63% {
    transform: rotate(-22deg);
  }
  64.01%,
  100% {
    transform: rotate(0deg);
  }
}
@keyframes ls-yard-ad-leg-b {
  0%,
  23.99% {
    transform: rotate(0deg);
  }
  24%,
  26%,
  28%,
  30%,
  32%,
  34%,
  36% {
    transform: rotate(-22deg);
  }
  25%,
  27%,
  29%,
  31%,
  33%,
  35% {
    transform: rotate(22deg);
  }
  36.01%,
  51.99% {
    transform: rotate(0deg);
  }
  52%,
  54%,
  56%,
  58%,
  60%,
  62%,
  64% {
    transform: rotate(-22deg);
  }
  53%,
  55%,
  57%,
  59%,
  61%,
  63% {
    transform: rotate(22deg);
  }
  64.01%,
  100% {
    transform: rotate(0deg);
  }
}
@keyframes ls-yard-ad-arm {
  0%,
  36.99% {
    transform: rotate(0deg);
  }
  37%,
  40%,
  43%,
  46% {
    transform: rotate(95deg);
  }
  38.5%,
  41.5%,
  44.5%,
  47.5% {
    transform: rotate(140deg);
  }
  48.01%,
  100% {
    transform: rotate(0deg);
  }
}
@keyframes ls-yard-shout {
  0%,
  36.9% {
    opacity: 0;
  }
  37%,
  48% {
    opacity: 1;
  }
  48.1%,
  100% {
    opacity: 0;
  }
}

/* kids: turn at 29-30%, run right out of the strip, come back at 82-92% */
@keyframes ls-yard-move-1 {
  0%,
  29% {
    transform: translateX(0);
  }
  38%,
  82% {
    transform: translateX(-420px);
  }
  92%,
  100% {
    transform: translateX(0);
  }
}
@keyframes ls-yard-move-2 {
  0%,
  29.5% {
    transform: translateX(0);
  }
  38.5%,
  64% {
    transform: translateX(-440px);
  }
  70%,
  82% {
    transform: translateX(-130px);
  }
  92%,
  100% {
    transform: translateX(0);
  }
}
@keyframes ls-yard-move-3 {
  0%,
  30% {
    transform: translateX(0);
  }
  39%,
  82% {
    transform: translateX(-470px);
  }
  92%,
  100% {
    transform: translateX(0);
  }
}
@keyframes ls-yard-face-1 {
  0% {
    transform: scaleX(1);
  }
  29% {
    transform: scaleX(-1);
  }
  82%,
  100% {
    transform: scaleX(1);
  }
}
@keyframes ls-yard-face-2 {
  0% {
    transform: scaleX(1);
  }
  29.5% {
    transform: scaleX(-1);
  }
  64% {
    transform: scaleX(1);
  }
  72.5% {
    transform: scaleX(-1);
  }
  74.5% {
    transform: scaleX(1);
  }
  77% {
    transform: scaleX(-1);
  }
  82%,
  100% {
    transform: scaleX(1);
  }
}
@keyframes ls-yard-face-3 {
  0% {
    transform: scaleX(1);
  }
  82% {
    transform: scaleX(-1);
  }
  92%,
  100% {
    transform: scaleX(1);
  }
}
/* one of them peeks back out to call the others */
@keyframes ls-yard-call {
  0%,
  76.9% {
    opacity: 0;
  }
  77%,
  83% {
    opacity: 1;
  }
  83.1%,
  100% {
    opacity: 0;
  }
}

/* ================= previous shell (other auth pages) ================= */
.link-text {
  color: #409fff;
  font-size: 14px;
}
.link-campus-info {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}
.entry-block__header {
  margin-bottom: 10vh;
}
.campus-info {
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
  flex-direction: column;
  padding-left: 40px;
  padding-right: 40px;
}
.campus-info__logo {
  min-height: 40px;
  max-width: 80px;
  object-fit: contain;
  width: 50%;
}
.campus-info__name {
  font-weight: 600;
  font-size: 24px;
}
@media (max-width: 420px) {
  .entry-block__left {
    display: none;
  }
  .entry-block__header {
    margin-bottom: 2vh;
  }
}
</style>
